import { useCallback, useRef, useState } from 'react';
import { ExperimentStatus, VariantType } from 'src/services';
import { UseVariantsStepReturnType, VariantsStepProps } from './types';

export const useVariantsStep = ({ experiment }: VariantsStepProps): UseVariantsStepReturnType => {
  const isProvisioning = experiment?.status === ExperimentStatus.Provisioning;
  const provisioningError = experiment?.provisioningError ?? undefined;
  const firstLever = experiment?.wizardSelection?.levers?.[0];
  const controlVariantId = experiment?.variants.find(
    variant => variant.type === VariantType.Control
  )?.id;
  const testVariant = experiment?.variants.find(variant => variant.type === VariantType.Test);
  const testVariantId = testVariant?.id;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // The active editor (merch rules / ranking strategy / view config) already persists straight
  // to the Test variant through its own existing CRUD endpoint - there is no separate wizard
  // "patch" to save here. `saveRef` just lets Continue flush whatever that editor still has
  // in flight (e.g. a debounced slider drag) before the wizard unmounts it and moves on, so a
  // fast Continue click right after an edit can't lose it.
  const saveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  const registerSave = useCallback((save: () => Promise<boolean>) => {
    saveRef.current = save;
  }, []);

  const onSubmit = useCallback((): Promise<boolean> => {
    setIsSubmitting(true);
    return saveRef
      .current()
      .catch(() => false)
      .finally(() => setIsSubmitting(false));
  }, []);

  return {
    controlVariantId,
    testVariantId,
    testVariant,
    feature: firstLever?.feature,
    surface: firstLever?.surface,
    isProvisioning,
    provisioningError,
    canContinue: !isProvisioning,
    isSubmitting,
    onSubmit,
    registerSave,
  };
};
